import React, { useEffect, useState, useMemo } from "react";
import LoggerService from "../services/LoggerService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import { Info, AlertTriangle, XCircle, Trash2, ChevronDown } from "lucide-react";

const LogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const data = await LoggerService.getLogs();
      setLogs(data);
    } catch (error) {
      console.error("Error fetching logs", error);
    }
  };

  const handleDeleteLogs = async () => {
    setIsDeleting(true);
    try {
      await LoggerService.deleteLogs();
      setLogs([]); // Clear logs from state after successful deletion
      setOpenDialog(false);
    } catch (error) {
      console.error("Error deleting logs", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const groupedLogs = useMemo(() => {
    return logs.reduce((acc, log) => {
      const match = log.match(/^(.*?) \[(.*?)\] (.*)$/);
      if (!match) return acc;

      const [, date, level, message] = match;
      if (!acc[level]) acc[level] = [];
      acc[level].push({ date, message });
      return acc;
    }, {});
  }, [logs]);

  const levelStyles = {
    INFO: { color: "#2196F3", icon: <Info size={isMobile ? 16 : 18} /> },
    WARN: { color: "#FF9800", icon: <AlertTriangle size={isMobile ? 16 : 18} /> },
    ERROR: { color: "#F44336", icon: <XCircle size={isMobile ? 16 : 18} /> },
  };

  // Function to format date for better mobile display
  const formatDate = (dateStr) => {
    if (isMobile) {
      // On mobile, show shorter date format
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return dateStr;
  };

  return (
    <Paper sx={{ 
      padding: { xs: 1, sm: 2, md: 3 },
      overflowX: "hidden",
      width: "100%" 
    }}>
      <Box sx={{ 
        display: "flex", 
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "space-between", 
        alignItems: isMobile ? "flex-start" : "center", 
        gap: isMobile ? 2 : 0,
        marginBottom: 2 
      }}>
        <Typography variant={isMobile ? "subtitle1" : "h6"} component="h2" sx={{ fontWeight: "bold" }}>
          System Logs
        </Typography>
        
        <Button
          variant="contained"
          color="error"
          size={isMobile ? "small" : "medium"}
          disabled={isDeleting || logs.length === 0}
          onClick={() => setOpenDialog(true)}
          startIcon={<Trash2 size={isMobile ? 16 : 18} />}
          sx={{ alignSelf: isMobile ? "flex-end" : "auto" }}
        >
          {isDeleting ? "Deleting..." : isMobile ? "Delete Logs" : "Delete All Logs"}
        </Button>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle id="alert-dialog-title">
            Delete All Logs
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure you want to delete all logs? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteLogs} 
              color="error" 
              variant="contained"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {Object.keys(groupedLogs).length === 0 && (
        <Typography variant="body2" sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          No logs available
        </Typography>
      )}

      {Object.entries(groupedLogs).map(([level, logEntries]) => (
        <Box key={level} sx={{ marginBottom: 3 }}>
          {/* Level Header */}
          <Box
            sx={{
              backgroundColor: levelStyles[level]?.color || "gray",
              color: "white",
              padding: { xs: "8px 12px", sm: "10px 15px" },
              display: "flex",
              alignItems: "center",
              gap: "10px",
              borderRadius: "4px",
            }}
          >
            {levelStyles[level]?.icon}
            <Typography 
              variant={isMobile ? "body2" : "subtitle1"} 
              sx={{ fontWeight: "bold" }}
            >
              {level} ({logEntries.length})
            </Typography>
          </Box>

          {/* Desktop and Tablet View */}
          {!isMobile && (
            <Box sx={{ overflowX: "auto" }}>
              <Table size={isTablet ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell width="5%" sx={{ whiteSpace: "nowrap" }}>#</TableCell>
                    <TableCell width={isTablet ? "30%" : "25%"} sx={{ whiteSpace: "nowrap" }}>Date</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logEntries.map((log, index) => (
                    <TableRow key={index} sx={{ backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white" }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontSize: isTablet ? "0.8rem" : "inherit" }}>{formatDate(log.date)}</TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: isTablet ? "0.8rem" : "inherit",
                          maxWidth: "100%", 
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                      >
                        {log.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Mobile View - Accordions */}
          {isMobile && (
            <Box sx={{ mt: 1 }}>
              {logEntries.map((log, index) => (
                <Accordion key={index} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #eee' }}>
                  <AccordionSummary
                    expandIcon={<ChevronDown size={16} />}
                    sx={{ 
                      backgroundColor: index % 2 === 0 ? "#f9f9f9" : "white",
                      padding: "0 12px"
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                      <Chip 
                        label={index + 1} 
                        size="small" 
                        sx={{ mr: 1, minWidth: 28, height: 20 }} 
                      />
                      <Typography variant="body2" sx={{ 
                        fontSize: "0.75rem", 
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "calc(100% - 40px)"
                      }}>
                        {formatDate(log.date)}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: "8px 12px", backgroundColor: "#fafafa" }}>
                    <Typography variant="body2" sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                      {log.message}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      ))}
    </Paper>
  );
};

export default LogViewer;